import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatMessage, EstimateSnapshot, ProposedAction, ToolCall } from '../types';
import { archiveSession, createSession, fetchCurrentSession, fetchSession } from '../api/client';
import { streamChat } from '../api/chat-stream';
import { refreshEstimateData } from '../api/refreshEstimate';

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

// Every mutating tool in the AI service writes only when called with
// `confirmed: true`; otherwise it just returns a proposed action.
function isConfirmedWrite(call: ToolCall): boolean {
  return call.args?.confirmed === true;
}

export function useChat(getEstimateSnapshot: () => EstimateSnapshot) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True until the resume lookup settles, so the panel can show a placeholder
  // rather than an empty-state that flashes and is immediately replaced.
  const [isHydrating, setIsHydrating] = useState(true);
  // How many leading messages came from a resumed transcript. Proposals inside them
  // are rendered read-only: a reload should not put "apply" one click away for a
  // mutation proposed hours ago, against an estimate that may have changed since.
  const [hydratedCount, setHydratedCount] = useState(0);
  // Keeps async stream callbacks from setting state on an unmounted panel.
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  // Entry guard for runTurn. `isStreaming` state can't do this job: it is captured
  // in runTurn's closure and only updates on the next render, so two interactions
  // in one React batch (fast double-Enter, or Enter racing an Apply) would both
  // read false and send a turn. The state is still what disables the UI.
  const streamingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // Sessions are created on demand, not when the drawer mounts: opening the panel
  // and typing nothing is the common case, and each of those would otherwise leave
  // an abandoned server session behind. `ensureSession` is deduped through a ref so
  // concurrent callers share one request, and clears it on failure so a later
  // attempt can retry instead of caching the error forever.
  const sessionPromiseRef = useRef<Promise<string> | null>(null);
  // Resolves to the resumed session id, or null if there was nothing to resume.
  // Separate from `sessionPromiseRef` because a resume must not create anything --
  // but every path to a session has to wait on it, or a composer focus landing
  // before the lookup returns would mint a second session and strand the thread.
  const resumePromiseRef = useRef<Promise<string | null> | null>(null);
  // Bumped by whichever call decides what the active thread is -- an explicit
  // history selection or a fresh "New Chat" -- so a slower-resolving lookup started
  // before that decision (the mount-time resume, or an earlier history click) can
  // tell it has been superseded and must not overwrite state with a thread the user
  // has since moved away from.
  const threadSeqRef = useRef(0);

  const resume = useCallback((): Promise<string | null> => {
    if (!resumePromiseRef.current) {
      const seq = threadSeqRef.current;
      resumePromiseRef.current = fetchCurrentSession()
        .then((resumed) => {
          if (!resumed || threadSeqRef.current !== seq) {
            return null;
          }
          if (mountedRef.current) {
            setMessages(resumed.messages);
            setHydratedCount(resumed.messages.length);
            setSessionId(resumed.sessionId);
          }
          return resumed.sessionId;
        })
        .catch(() => {
          // A failed resume is not a failed session: fall through to creating a
          // fresh one rather than leaving the user unable to chat at all.
          return null;
        });
    }
    return resumePromiseRef.current;
  }, []);

  useEffect(() => {
    resume().finally(() => {
      if (mountedRef.current) {
        setIsHydrating(false);
      }
    });
  }, [resume]);

  const ensureSession = useCallback((): Promise<string> => {
    if (!sessionPromiseRef.current) {
      const seq = threadSeqRef.current;
      sessionPromiseRef.current = resume()
        .then((resumed) => resumed ?? createSession())
        .then((id) => {
          // Superseded by an explicit selection while this was in flight: the id
          // this resolves to is still returned (a turn already waiting on it needs
          // one), but it must not become the displayed session.
          if (mountedRef.current && threadSeqRef.current === seq) {
            setSessionId(id);
          }
          return id;
        })
        .catch((err: Error) => {
          sessionPromiseRef.current = null;
          if (mountedRef.current) {
            setError(`Failed to create session: ${err.message}`);
          }
          throw err;
        });
    }
    return sessionPromiseRef.current;
  }, [resume]);

  // Abandons the current thread and starts an empty one. The new session itself is
  // created lazily, as ever, so clicking this and typing nothing costs nothing.
  const startNewSession = useCallback(() => {
    abortRef.current?.abort();
    streamingRef.current = false;
    const previous = sessionId;
    threadSeqRef.current += 1;
    sessionPromiseRef.current = null;
    // Pin the resume to "nothing", or ensureSession would chain off the earlier
    // lookup and drop the user straight back into the thread they just left.
    resumePromiseRef.current = Promise.resolve(null);
    setSessionId(null);
    setMessages([]);
    setHydratedCount(0);
    setError(null);
    setIsStreaming(false);
    // The mount-time resume may still be in flight (its own `.finally` won't run
    // until that fetch settles); without this, a "New Chat" clicked in that window
    // leaves the panel showing "Loading your conversation…" instead of the empty
    // state, since it's now empty *and* still nominally hydrating.
    setIsHydrating(false);
    if (previous) {
      // One retry, best-effort beyond that: a dropped archive leaves the old thread
      // resumable, which would silently undo "New Chat" on the next reload with no
      // sign anything went wrong. The new (already-empty) thread isn't blocked on
      // the outcome -- this only surfaces a warning if both attempts fail.
      archiveSession(previous)
        .catch(() => archiveSession(previous))
        .catch(() => {
          if (mountedRef.current) {
            setError(
              'Could not archive the previous chat -- it may reappear as your most recent one.'
            );
          }
        });
    }
  }, [sessionId]);

  // Loads a past conversation from the history list and makes it the active thread,
  // so the user can carry on where they left off. The service treats the fetch as a
  // pure read -- opening a conversation does not promote it -- and the promotion
  // happens on the first message sent into it, like any other turn.
  const openSession = useCallback(async (id: string): Promise<boolean> => {
    abortRef.current?.abort();
    streamingRef.current = false;
    setIsStreaming(false);
    setError(null);
    // Claim the active thread now, not after the fetch resolves: a second row
    // clicked before this fetch settles must win regardless of which fetch finishes
    // first, and a still-pending mount-time resume must not clobber this selection.
    const seq = ++threadSeqRef.current;
    try {
      const restored = await fetchSession(id);
      if (!mountedRef.current || threadSeqRef.current !== seq) {
        return false;
      }
      setMessages(restored);
      // Everything loaded counts as restored, so its proposals render read-only for
      // the same reason a resumed thread's do.
      setHydratedCount(restored.length);
      setSessionId(id);
      // Point both guards at this conversation: without it the next send would fall
      // back to the earlier resume lookup and stream into a different thread.
      sessionPromiseRef.current = Promise.resolve(id);
      resumePromiseRef.current = Promise.resolve(id);
      return true;
    } catch (err) {
      if (mountedRef.current && threadSeqRef.current === seq) {
        setError(err instanceof Error ? err.message : String(err));
      }
      return false;
    }
  }, []);

  // Cancels an in-flight turn, so a stream can't keep the input disabled unseen.
  const cancelTurn = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const runTurn = useCallback(
    async (text: string, opts: { refreshOnDone?: boolean } = {}): Promise<boolean> => {
      const { refreshOnDone } = opts;
      if (streamingRef.current) {
        return false;
      }
      streamingRef.current = true;

      // Create the session now if the composer's focus handler hasn't already
      // warmed one. A send is never dropped for want of a session -- it waits.
      let activeSessionId: string;
      try {
        activeSessionId = await ensureSession();
      } catch {
        // ensureSession has already surfaced the reason in `error`.
        streamingRef.current = false;
        return false;
      }

      // Ids mirror how the service rehydrates a stored transcript (`{sessionId}:{index}`)
      // so a message keeps its id across a reload. They are the feedback record's sort
      // key, so a random id would orphan a rating the moment the page reloads.
      // (A turn that errors is not persisted server-side, so ids can drift by one pair
      // for the rest of that page view; the next load re-derives them from the
      // transcript.)
      setMessages((prev) => [
        ...prev,
        { id: `${activeSessionId}:${prev.length}`, role: 'user', content: text },
        {
          id: `${activeSessionId}:${prev.length + 1}`,
          role: 'assistant',
          content: '',
          actions: [],
          toolCalls: [],
        },
      ]);
      setIsStreaming(true);
      setError(null);

      const patchLast = (patch: (msg: ChatMessage) => ChatMessage) =>
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          return [...prev.slice(0, -1), patch(last)];
        });

      const controller = new AbortController();
      abortRef.current = controller;
      let succeeded = false;
      // The assistant can execute a write without an Apply click, so a turn that ran
      // one needs a refresh even outside the `refreshOnDone` path. Read-only tools
      // (get_estimate, list_estimates, search_knowledge_base, ...) and unconfirmed
      // mutating calls, which only stage a proposal, must not trigger one.
      let sawWrite = false;

      // Keep the first error of the turn. A stream can emit an `error` event and then
      // have the connection drop (a throw in the catch below); the SSE error is the
      // more specific diagnostic, so a later generic failure must not overwrite it.
      let turnErrored = false;
      const recordError = (message: string) => {
        if (turnErrored) {
          return;
        }
        turnErrored = true;
        setError(message);
      };

      try {
        await streamChat(
          {
            sessionId: activeSessionId,
            message: text,
            estimateId: getEstimateSnapshot()?.estimateId,
            signal: controller.signal,
          },
          {
            onTextDelta: (delta: string) =>
              patchLast((last) => ({ ...last, content: last.content + delta })),
            onProposedAction: (action: ProposedAction) =>
              patchLast((last) => ({ ...last, actions: [...(last.actions || []), action] })),
            onToolCall: (call: ToolCall) => {
              if (isConfirmedWrite(call)) {
                sawWrite = true;
              }
              patchLast((last) => ({ ...last, toolCalls: [...(last.toolCalls || []), call] }));
            },
            onError: (err: string) => recordError(err),
            onDone: (ok: boolean) => {
              succeeded = ok;
              // Refresh the CE UI to reflect the mutation the assistant applied. Only
              // on success: a failed turn changed nothing, so refetching would imply
              // the apply worked.
              if (ok && (refreshOnDone || sawWrite)) {
                refreshEstimateData();
              }
            },
          }
        );
      } catch (err) {
        // An abort is a deliberate cancel, not a failure worth showing the user.
        if (!isAbortError(err) && mountedRef.current) {
          recordError(err instanceof Error ? err.message : String(err));
        }
        succeeded = false;
      } finally {
        streamingRef.current = false;
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        if (mountedRef.current) {
          setIsStreaming(false);
        }
      }

      return succeeded;
    },
    [ensureSession, getEstimateSnapshot]
  );

  const sendMessage = useCallback((text: string) => runTurn(text), [runTurn]);

  // Apply/Reject from an ActionCard. Resolves to whether the turn succeeded, so the
  // card can report the real outcome instead of assuming success.
  const resolveAction = useCallback(
    (decision: 'yes' | 'no') => runTurn(decision, { refreshOnDone: decision === 'yes' }),
    [runTurn]
  );

  return {
    messages,
    sendMessage,
    resolveAction,
    cancelTurn,
    startNewSession,
    openSession,
    // Called when the composer gains focus, to warm a session while the user types
    // so the first send doesn't pay for the round trip.
    warmSession: ensureSession,
    isStreaming,
    isHydrating,
    hydratedCount,
    error,
    sessionId,
  };
}
